import { $, $$, body } from "@utils/dom";
import { on, off } from "@utils/listener";
import { mobile } from "@utils/mobile";
import Throttle from "@utils/throttle";

import MaskMediaCircle from "@components/MaskMediaCircle";

export const SELECTOR = "[data-site-super-menu]";
export const NAV_SELECTOR = ".site-super-menu__nav";
export const PROMO_CIRCLE_SELECTOR = ".site-super-menu__promo__img";
export const MENU_DELAY = 250;

class SiteSuperMenu {
  constructor(init = false) {
    this.initialized = false;
    this.opened = false;
    this.el = null;

    this.currentId = null;
    this.currentDelay = MENU_DELAY;

    init ? this.init() : null;

    this._onEnter = this._onEnter.bind(this);
    this._onMobileClick = this._onMobileClick.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._open = this._open.bind(this);
    this._selectCurrentNav = this._selectCurrentNav.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._onLeaveUnlessOpened = this._onLeaveUnlessOpened.bind(this);
    this._close = this._close.bind(this);
    this._toggle = this._toggle.bind(this);
    this._animatePromoCircles = this._animatePromoCircles.bind(this);
    this._resetPromoCircles = this._resetPromoCircles.bind(this);
  }

  get name() {
    return `SiteSuperMenu`;
  }

  init() {
    this.initialized = true;
    this.el = $(SELECTOR);

    this.backdrop = $('.site-super-menu__backdrop', this.el);
    this.container = $('.site-super-menu__container', this.el);
    this.triggers = [...$$(`[aria-controls="${this.el.id}"]`)];
    this.navs = [...$$(NAV_SELECTOR, this.el)];

    this.promoCircles = [...$$(PROMO_CIRCLE_SELECTOR, this.el)].map(el => new MaskMediaCircle(el));
    this.navThrottler = new Throttle({cb: this._toggle, delay: 250, onlyAtEnd: false});

    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    this.el = null;
    this.initialized = false;
    this.opened = false;

    this.backdrop = null;
    this.container = null;
    this.triggers = null;
    this.navs = null;
    this.promoCircles = null;

    if(this.navThrottler) this.navThrottler.dispose();
    this.navThrottler = null;

    this.currentId = null;
    this.currentDelay = null;
  }

  _bindEvents() {
    if( mobile ) {
      if( this.triggers ) on(this.triggers, 'click', this._onMobileClick);
      if( this.backdrop ) on(this.backdrop, 'click', this._onBackdropClick);
    } else {
      if( this.triggers ) on(this.triggers, 'mouseenter', this._onEnter);
      if( this.triggers ) on(this.triggers, 'mouseleave', this._onLeaveUnlessOpened);
      if( this.container ) on(this.container, 'mouseleave', this._onLeave);
    }
    
    this.emitter.on(`SiteScroll.direction`, this._onLeave);
  }
  _unbindEvents() {
    if( mobile ) {
      if( this.triggers ) off(this.triggers, 'click', this._onMobileClick);
      if( this.backdrop ) off(this.backdrop, 'click', this._onBackdropClick);
    } else {
      if( this.triggers ) off(this.triggers, 'mouseenter', this._onEnter);
      if( this.triggers ) off(this.triggers, 'mouseleave', this._onLeaveUnlessOpened);
      if( this.container ) off(this.container, 'mouseleave', this._onLeave);
    }
    
    this.emitter.off(`SiteScroll.direction`, this._onLeave);
  }

  _onEnter(e){
    this.currentId = e.currentTarget.getAttribute('data-nav-id');
    this.navThrottler.init();
  }
  _onMobileClick(e) {
    if( e ) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    
    const navID = e.currentTarget.getAttribute('data-nav-id');

    this.currentId = this.currentId !== navID ? navID : null;
    this._toggle();

    return false;
  }
  _onBackdropClick(e) {
    if( e ) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }

    this.currentId = null;
    this._toggle();

    return false;
  }

  _selectCurrentNav(){
    this.navs.forEach((_el) => {
      if (_el.getAttribute('data-id') == this.currentId) {
        _el.setAttribute('aria-hidden', false);
        this._animatePromoCircles(_el);
      } else {
        _el.setAttribute('aria-hidden', true);
        this._resetPromoCircles(_el);
      }
    });

    this.triggers.forEach((_el) => {
      if (_el.getAttribute('data-nav-id') == this.currentId) {
        _el.setAttribute('aria-expanded', true);
      } else {
        _el.setAttribute('aria-expanded', false);
      }
    });
  }

  _animatePromoCircles(scope){
    [...$$(PROMO_CIRCLE_SELECTOR, scope)].forEach((_el) => {
      const promoCircleItem = this.promoCircles.find(item => item.el === _el);
      if( !promoCircleItem ) return;

      const animationDelay = (parseInt(getComputedStyle(_el).getPropertyValue('--index')) * 150) + this.currentDelay;

      // promoCircleItem.reset();
      promoCircleItem.animate(animationDelay);
    });
  }
  _resetPromoCircles(scope){
    [...$$(PROMO_CIRCLE_SELECTOR, scope)].forEach((_el) => {
      const promoCircleItem = this.promoCircles.find(item => item.el === _el);
      if( !promoCircleItem ) return;

      promoCircleItem.reset();
    });
  }

  _onLeave(){
    this.currentId = null;
    this.navThrottler.init();
  }
  _onLeaveUnlessOpened(){
    if(!this.opened){
      this._onLeave();
    }
  }

  _open(){
    this.opened = true;

    body.classList.add('--js-site-super-menu-opened');
    this.el.setAttribute('aria-hidden', false);

    // remove delay after element open
    setTimeout(()=> {
      this.currentDelay = 0;
      this.el.style.setProperty('--menuDelay',`${this.currentDelay}ms`);
    }, 50);
  }
  _close(){
    this.opened = false;

    body.classList.remove('--js-site-super-menu-opened');
    this.el.setAttribute('aria-hidden', true);

    // add delay after element open
    this.currentDelay = MENU_DELAY;
    this.el.style.setProperty('--menuDelay',`${this.currentDelay}ms`);
  }
  _toggle(){
    this._selectCurrentNav();
    if(this.currentId){
      this._open();
    } else {
      this._close();
    }
  }
}

export default SiteSuperMenu;
